import axios from 'axios';
import { useCallback } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { ProductCategory } from '../../enums/ProductCategory.enum';
import { ProductHandle } from '../../enums/ProductHandle.enum';
import { SubContext } from '../../enums/SubContext.enum';
import { DataLayerProduct, DataLayerVariant } from '../../models/DataLayer.model';
import { ShopifyCart } from '../../models/ShopifyCart.model';
import { LineItemProperty, ShopifyDiscount, ShopifyLineItem } from '../../models/ShopifyLineItem.model';
import { ViewVariant } from '../../models/ShopifyVariant.model';
import { ViewProduct, ViewProducts } from '../../models/ViewProduct.model';
import { environmentContextState, shopifyState } from '../../recoil/atoms';
import { cartState, productsState } from '../../recoil/selectors';
import { isDefined } from '../../sizing-tool/utils/array-helper';
import { trackCartItemAdded, trackCartLineItemAdded, trackCartLineItemRemoved, trackCartPromoClicked } from '../../tracking/events';

export const useGetCartAction = (): (() => Promise<void>) => {
  const set = useSetRecoilState(cartState);
  return async () => {
    await getCart()
      .then((response) => {
        set(normalizeCart(response));
      })
      .catch(() => {
        // eslint-disable-next-line no-console
        console.log('Error fetching cart');
      });
  };
};

export const useClearCartAction = (): (() => Promise<void>) => {
  const [previousState, set] = useRecoilState(cartState);

  return async () => {
    set({ ...previousState, loading: true });
    await axios
      .post<ShopifyCart>(
        '/cart/clear.js',
        {},
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      )
      .then((response) => {
        set(normalizeCart(response));
      })
      .catch((error) => {
        set({ ...previousState, error, loading: false });
      });
  };
};

type LineItemQuanties = Record<string, number>;

export const useUpdateCartAction = (): ((lineItemQuantities: LineItemQuanties) => Promise<void>) => {
  const [previousState, set] = useRecoilState(cartState);
  const shopify = useRecoilValue(shopifyState);

  return useCallback(
    async (lineItemQuantities: LineItemQuanties) => {
      set({ ...previousState, loading: true });
      // FIXME: Add Track FN for each line item
      const payload = {
        updates: lineItemQuantities,
      };

      try {
        const response = await axios.post('/cart/update.js', payload, { headers: { 'Content-Type': 'application/json' } });
        set(normalizeCart(response));
      } catch (error) {
        set({ ...previousState, error: JSON.stringify(error), loading: false });
      }
    },
    [previousState, set],
  );
};

export const useAdjustCartQuantityAction = (): ((lineItem: ShopifyLineItem, modifier: number) => Promise<void>) => {
  const [previousState, set] = useRecoilState(cartState);
  const shopify = useRecoilValue(shopifyState);

  return async (lineItem: ShopifyLineItem, modifier: number) => {
    set({ ...previousState, loading: true });
    const trackFn = modifier > 0 ? trackCartLineItemAdded : trackCartLineItemRemoved;
    trackFn(shopify, lineItemToProduct(lineItem));
    const payload = {
      id: lineItem.key,
      quantity: lineItem.quantity + modifier,
    };

    try {
      const response = await axios.post('/cart/change.js', payload, { headers: { 'Content-Type': 'application/json' } });
      set(normalizeCart(response));
    } catch (error) {
      set({ ...previousState, error: JSON.stringify(error), loading: false });
    }
  };
};

const lineItemToProduct = (lineItem: ShopifyLineItem) => ({
  ...lineItem,
  price: lineItem?.price && lineItem.price / 100,
});

export interface ItemForCart {
  productHandle: ProductHandle | undefined;
  variant: ViewVariant;
  quantity: number;
  properties?: Partial<Record<LineItemProperty, string>>;
}

export const useAddToCartAction = (): ((items: ItemForCart[], subContext: SubContext) => Promise<ShopifyCart>) => {
  const [previousState, set] = useRecoilState(cartState);
  const allProducts = useRecoilValue(productsState);
  const shopify = useRecoilValue(shopifyState);
  const environmentContext = useRecoilValue(environmentContextState);
  const warehouseDiscountActive = environmentContext.warehouseSaleEnabled;
  const warehouseDiscountPercentage = environmentContext.warehouseDiscountPercentage;

  return useCallback(
    async (items: ItemForCart[], subContext: SubContext): Promise<ShopifyCart> => {
      set({ ...previousState, loading: true });

      items.forEach((i) => {
        const product = Object.values(allProducts).find((p) => p.variants.some((v) => v.id === i.variant.id));

        i.properties = { ...i.properties, _PRODUCT_HANDLE: i.productHandle };
        i.properties = warehouseDiscountActive
          ? setLineItemReturnStatus(environmentContext.warehouseSaleSKUs, warehouseDiscountPercentage, i.variant, product, i.properties)
          : i.properties;
        i.properties =
          environmentContext.timedSaleActive && !i.properties?.Bundle && !i.properties?._DISCOUNT_TYPE
            ? {
                ...i.properties,
                _DISCOUNT_TYPE: 'PROMO',
              }
            : i.properties;
      });

      try {
        await addToCart(items);
        const response = await getCart();
        set(normalizeCart(response));
        // tracking
        subContext === SubContext.PromoCartUpsell && trackCartPromoClicked();

        items.forEach((i) => {
          const product = getProductFromVariant(allProducts, i.variant);
          if (product?.type !== ProductCategory.AddOns) {
            trackCartItemAdded(shopify, product as DataLayerProduct, i.variant as DataLayerVariant, i.quantity);
          }
        });

        return normalizeCart(response);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log('Error fetching cart', error);
        const response = { ...previousState, error: error as string, loading: false };
        set(response);
        return response;
      }
    },
    [
      allProducts,
      environmentContext.timedSaleActive,
      environmentContext.warehouseSaleSKUs,
      previousState,
      set,
      shopify,
      warehouseDiscountActive,
      warehouseDiscountPercentage,
    ],
  );
};

export const noReturnLineItemPropertyKey = '_RR_SERVICEABILITY';
const setLineItemReturnStatus = (
  warehouseSaleSKUs: string[],
  warehouseDiscountPercentage: number,
  { sku }: ViewVariant,
  product: ViewProduct | undefined,
  properties: Partial<Record<LineItemProperty, string>> = {},
) => {
  const warehouseSaleProperty = { [`_WH${warehouseDiscountPercentage}`]: 'TRUE', _DISCOUNT_TYPE: 'WH30' };
  const noReturnLineItemProperty = { [noReturnLineItemPropertyKey]: 'BLOCK_ALL' };

  const isWarehouseSaleItem = warehouseSaleSKUs.includes(sku);

  const updatedProperties = [
    isWarehouseSaleItem ? warehouseSaleProperty : undefined,
    !product?.isReturnable || isWarehouseSaleItem ? noReturnLineItemProperty : undefined,
  ]
    .filter(isDefined)
    .reduce((acc, curr) => ({ ...acc, ...curr }), {});

  return { ...properties, ...updatedProperties };
};

const getCart = () => axios.get<ShopifyCart>('/cart.js');

const addToCart = (items: ItemForCart[]) => {
  const newLineItems = items.map((i) => ({ id: +i.variant.id, quantity: i.quantity, properties: i.properties }));
  const payload = { items: newLineItems };
  return axios.post<ShopifyCart>('/cart/add.js', payload, { headers: { 'Content-Type': 'application/json' } }).catch((err) => {
    // eslint-disable-next-line no-console
    console.error('Error adding item', err);
  });
};

export const getProductFromVariant = (allProducts: ViewProducts, variant: ViewVariant): ViewProduct | undefined =>
  (Object.values(allProducts) as ViewProduct[]).find((p) => String(p.id) === String(variant.featured_image.product_id));

const normalizeCart = ({ data }: any): ShopifyCart => {
  // TODO remove / 100 when migrating to next
  // createdAt not available via cart.js
  return {
    id: data.id ?? data.token,
    loading: false,
    token: data.token,
    note: data.note,
    subtotalPrice: data.original_total_price / 100,
    totalPrice: data.total_price / 100,
    itemCount: data.item_count,
    lineItems: data.items.map(normalizeLineItem),
    currency: { code: data.currency },
    createdAt: data.createdAt,
    taxesIncluded: data.taxesIncluded ?? true,
    lineItemsSubtotalPrice: data.items_subtotal_price / 100,
  };
};

const normalizeLineItem = (item: ShopifyLineItem) => {
  return {
    ...item,
    properties: item.properties ?? {},
    discounts: item.discounts.map(normalizeDiscount),
    final_line_price: item.final_line_price / 100,
    original_line_price: item.original_line_price / 100,
  };
};

const normalizeDiscount = (discount: ShopifyDiscount): ShopifyDiscount => {
  return {
    ...discount,
    amount: discount.amount / 100,
  };
};
